<template>
  <div style="margin-top: 20px">
    <FiPageRail title="SOS Yard Map" nowrapActions>
      <template #actions>
        <b-button size="sm" variant="outline-secondary" @click="showInfo = true" title="About this page" aria-label="About this page">
          <i class="fa fa-info-circle"></i>
        </b-button>
      </template>
    </FiPageRail>

    <b-modal v-model="showInfo" title="About the SOS Yard Map" ok-only ok-title="Close">
      <p>An overlay map of your live vehicles, your yards, other Fleet Integrations customers' yards, and motorcoach association yards.</p>
      <ul>
        <li>Toggle layers in the left panel — <strong>click a layer row</strong> to show only it, or its <strong>checkbox</strong> to toggle just that layer.</li>
        <li><strong>Click a section heading</strong> to turn the whole group on or off.</li>
        <li>Fleet Int customers who opted in to the network show as <em>Bus SOS Partner Yards</em>; other companies' and association yards show as <em>Public yards</em>.</li>
        <li>Search the <em>Shown on map</em> list to find and zoom to any vehicle or yard.</li>
        <li><strong>Cluster</strong> collapses crowded spots into a count bubble; click one to zoom in.</li>
      </ul>
    </b-modal>

    <div class="overlay-wrap">
      <div class="overlay-panel">
        <div class="overlay-panel-title">Map layers</div>

        <div class="overlay-group">
          <div class="overlay-group-title">Display</div>
          <div class="overlay-layer" title="Collapse crowded spots into count bubbles; click a bubble to zoom in" @click="cluster = !cluster">
            <input type="checkbox" v-model="cluster" title="Cluster" @click.stop/>
            <span class="overlay-layer-label">Cluster</span>
          </div>
        </div>

        <div class="overlay-group overlay-range">
          <div class="overlay-group-title">Range filter</div>
          <div v-if="!range_pin" class="overlay-hint overlay-hint--tight">
            Click the map to drop a pin &amp; filter
          </div>
          <template v-else>
            <div class="overlay-range-row">
              <span class="overlay-range-label">Within <strong>{{ rangeLabel }}</strong></span>
              <button type="button" class="overlay-range-clear" @click="clear_range_pin"
                      title="Clear the range pin" aria-label="Clear the range pin">
                <i class="fa fa-times"></i>
              </button>
            </div>
            <input type="range" min="300" max="241402" step="100" v-model.number="range_radius_m"
                   class="overlay-range-slider" aria-label="Range radius in meters"/>
            <div class="overlay-hint overlay-hint--tight">{{ dedupedItems.length }} in range</div>
          </template>
        </div>

        <div class="overlay-group">
          <div class="overlay-range-row">
            <span class="overlay-range-label">Marker size <strong>{{ marker_size }} px</strong></span>
          </div>
          <input type="range" min="6" max="28" step="1" v-model.number="marker_size"
                 class="overlay-range-slider" aria-label="Marker size in pixels"/>
        </div>

        <div v-if="!yard_only" class="overlay-group">
          <button type="button" class="overlay-group-title overlay-group-title--btn"
                  title="Toggle this section" @click="toggle_section('vehicles')">Live positions</button>
          <div class="overlay-layer" :class="{ 'overlay-layer--pending': !vehicles.length && !vehicles_loading }"
               title="Show only this layer" @click="isolate('vehicles')">
            <input type="checkbox" v-model="show_vehicles" :disabled="!vehicles.length"
                   title="Toggle this layer" @click.stop/>
            <span class="overlay-swatch" style="background: #16a085"></span>
            <span class="overlay-layer-label">My vehicles</span>
            <b-spinner v-if="vehicles_loading" small></b-spinner>
            <span v-else class="overlay-count">{{ vehicles.length }}</span>
          </div>
          <div v-if="!vehicles_loading && !vehicles.length" class="overlay-hint overlay-hint--tight">
            <template v-if="yard_only">Vehicle positions aren't tracked for your company (yard-only provider).</template>
            <template v-else>No live vehicle positions (telematics not reporting).</template>
          </div>
        </div>

        <div class="overlay-group">
          <div class="overlay-group-head">
            <button type="button" class="overlay-group-title overlay-group-title--btn"
                    title="Toggle this section" @click="toggle_section('yards')">Yard locations</button>
            <button type="button" class="overlay-mini-btn" title="Show every yard layer"
                    :disabled="yardsAllOn" @click="set_section('yards', true)">All</button>
            <button type="button" class="overlay-mini-btn" title="Hide every yard layer"
                    :disabled="!yardsAnyOn" @click="set_section('yards', false)">None</button>
          </div>
          <div class="overlay-layer" title="Show only this layer" @click="isolate('own')">
            <input type="checkbox" v-model="show_own" title="Toggle this layer" @click.stop/>
            <span class="overlay-swatch" style="background: #2f7d4f"></span>
            <span class="overlay-layer-label">My yards</span>
            <span class="overlay-count">{{ own_yards.length }}</span>
          </div>
          <div class="overlay-layer" title="Show only this layer" @click="isolate('partner')">
            <input type="checkbox" v-model="show_partner" title="Toggle this layer" @click.stop/>
            <span class="overlay-swatch" style="background: #e67e22"></span>
            <span class="overlay-layer-label">Bus SOS Partner Yards</span>
            <span class="overlay-count">{{ partnerYards.length }}</span>
          </div>
          <div class="overlay-layer" title="Show only this layer" @click="isolate('public')">
            <input type="checkbox" v-model="show_public" title="Toggle this layer" @click.stop/>
            <span class="overlay-swatch" style="background: #23608f"></span>
            <span class="overlay-layer-label">Public yards</span>
            <span class="overlay-count">{{ publicYards.length }}</span>
          </div>
        </div>

        <div class="overlay-hint">
          Association &amp; customer yard data fills in as rosters are imported.
        </div>

        <div class="overlay-group overlay-shown">
          <div class="overlay-group-title overlay-shown-head">
            <span>Shown on map</span>
            <span class="overlay-count">
              <template v-if="search.trim()">{{ filteredItems.length }} of {{ dedupedItems.length }}</template>
              <template v-else>{{ dedupedItems.length }}</template>
            </span>
            <button type="button" class="overlay-mini-btn overlay-shown-dl" :disabled="!downloadItems.rows.length"
                    :title="'Download the ' + downloadItems.rows.length + ' ' + (downloadItems.yardsMode ? 'yard' : 'vehicle') + '(s) in the radius as a CSV'"
                    aria-label="Download the items in the radius as CSV" @click="download_csv">
              <i class="fa fa-download"></i>
            </button>
          </div>
          <input type="text" v-model="search" class="overlay-search"
                 placeholder="Search shown locations…" aria-label="Search shown locations"/>
          <div class="overlay-shown-list">
            <button v-for="m in filteredItems" :key="m.i" type="button" class="overlay-shown-row"
                    @click="focus_item(m)" :title="m.label">
              <span class="overlay-swatch" :style="{ background: m.color }"></span>
              <span class="overlay-shown-text">
                <span class="overlay-shown-name">{{ m.label }}</span>
                <span v-if="m.sublabel" class="overlay-shown-sub">{{ m.sublabel }}</span>
              </span>
              <span v-if="m.source" class="overlay-shown-src" :style="{ color: m.color }">{{ m.source }}</span>
            </button>
            <div v-if="!filteredItems.length" class="overlay-hint overlay-hint--tight">
              {{ markers.length ? 'No locations match.' : 'No layers selected.' }}
            </div>
          </div>
        </div>
      </div>

      <div class="overlay-mapwrap">
        <div v-if="loadError" class="overlay-state overlay-state--error">
          Couldn't load map data. Try again or contact support.
        </div>
        <gmap-map
            ref="overlayMap"
            :zoom="map_zoom"
            :center="map_center"
            :options="themed_map_options"
            class="overlay-map"
            @click="place_range_pin"
            @zoom_changed="on_zoom">
          <gmap-circle v-if="range_pin" :center="range_pin" :radius="rangeMeters"
                       :options="{ strokeColor: '#c0392b', strokeOpacity: 0.6, strokeWeight: 1, fillColor: '#c0392b', fillOpacity: 0.06, clickable: false }"/>
          <gmap-marker v-if="range_pin" :position="range_pin" :icon="rangePinIcon" :draggable="true"
                       :z-index="9999" @dragend="place_range_pin"/>
          <gmap-marker
              v-for="(m, idx) in displayMarkers"
              :key="idx"
              :position="m.position"
              :icon="m.icon"
              :label="m.count ? { text: String(m.count), color: '#ffffff', fontSize: '11px', fontWeight: '700' } : undefined"
              :clickable="true"
              @mouseover="openInfo(idx)"
              @click="m.count ? zoom_into(m) : pick_marker(idx, m)"
              @mouseout="info.open = false"/>
          <gmap-info-window
              :position="info.position"
              :opened="info.open"
              :options="{ pixelOffset: { width: 0, height: -34 } }">
            <div v-html="info.html"></div>
          </gmap-info-window>
        </gmap-map>
      </div>
    </div>
  </div>
</template>

<script>
import { get_sos_overlay, get_vehicle_map, get_sos_settings } from '../networking/apis'
import { mapState } from 'vuex'
import { DARK_MAP_STYLES } from '../assets/dark_map_styles'

// the map opens on the continental US, centered on the lower 48 at a zoom that frames it on a
// typical desktop pane (same view as the admin fleet map); it refits only after a filter change
const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 }
const DEFAULT_ZOOM = 4
// default marker edge in css px. the pin path is a 6-unit square, so the Google Symbol
// scale is px / 6. was a fixed 1.35 (~8px); now larger by default and user-resizable via
// the overlay slider, matching the Admin Tools Fleet Map's marker-size control.
const DEFAULT_MARKER_PX = 14

// clustering: screen-space grid cells of this many px at the current zoom; a cell holding at
// least CLUSTER_MIN markers renders as one count bubble (same rule as the admin fleet map)
const CLUSTER_CELL_PX = 40
const CLUSTER_MIN = 3

// google's web-mercator "world" coordinate (256px world) so cells can be sized in screen px
function worldPoint(lat, lng) {
  const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999)
  return {
    wx: 256 * (0.5 + lng / 360),
    wy: 256 * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
  }
}

export default {
  name: 'sos_map_page',
  data() {
    return {
      loadError: false,
      showInfo: false,
      range_pin: null,
      range_radius: 25,
      marker_size: DEFAULT_MARKER_PX,
      map_center: DEFAULT_CENTER,
      map_zoom: DEFAULT_ZOOM,
      own_yards: [],
      network_yards: [],
      show_own: true,
      show_partner: true,
      show_public: true,
      cluster: true,
      current_zoom: 4,
      vehicles: [],
      show_vehicles: true,
      vehicles_loading: false,
      // yard-only provider (no telematics): the "My vehicles" layer starts unchecked
      yard_only: false,
      search: '',
      info: { open: false, position: null, html: '' },
      // matches the fleet map basemap (vehicle_map.vue); midnight users get the dark variant
      mapStyle: {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'landscape', stylers: [{ color: '#F3F4F9' }] },
          { featureType: 'water', stylers: [{ color: '#C1CDE4' }] },
          { featureType: 'landscape.natural.terrain', stylers: [{ color: '#E0EDCA' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#DDDDDD' }] },
          { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#FFFFFF' }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#FEF8D6' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        ],
      },
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    themed_map_options() {
      const midnight = (this.$store.state.profile || {}).site_theme === 'midnight'
      return midnight ? { ...this.mapStyle, styles: DARK_MAP_STYLES } : this.mapStyle
    },
    partnerYards() {
      // Bus SOS partners: other Fleet Int customers who OPTED IN to the SOS network (their rows
      // carry a tenant_id). The viewing tenant's own yards and non-opted-in customers are excluded.
      const me = this.user && this.user.tenant
      return this.network_yards.filter(y => y.tenant_id && y.tenant_id !== me && y.opted_in)
    },
    publicYards() {
      // association / directory yards (no tenant_id), shown as one layer with no per-org split.
      // Fleet Int partner yards are a separate layer (see partnerYards).
      return this.network_yards.filter(y => !y.tenant_id)
    },
    markers() {
      const out = []
      if (this.show_own) {
        this.own_yards.forEach(y => this.pushMarker(out, y, '#2f7d4f', y.yard_name, y.address, y.phone, y.contact_name, 'My yard'))
      }
      if (this.show_partner) {
        this.partnerYards.forEach(y => this.pushMarker(out, y, '#e67e22', y.company, y.address, y.phone, 'Bus SOS partner', 'Partner yard'))
      }
      if (this.show_public) {
        this.publicYards.forEach(y => this.pushMarker(out, y, '#23608f', y.company, y.address, y.phone, '', 'Public yard'))
      }
      if (this.show_vehicles) {
        this.vehicles.forEach(v => this.pushMarker(out, v, '#16a085', v.name, v.location, '', 'My vehicle', 'My vehicle'))
      }
      // range filter: when a pin is dropped, only show markers within the radius
      if (this.range_pin) {
        return out.filter(m => this.distanceMiles(this.range_pin, m.position) <= this.range_radius)
      }
      return out
    },
    displayMarkers() {
      // what the map renders: the markers, or with clustering on, crowded spots collapsed into
      // one count bubble per CLUSTER_CELL_PX screen cell at the current zoom (click to zoom in).
      // the Shown on map list keeps listing the individual markers
      if (!this.cluster) return this.markers
      const scale = Math.pow(2, this.current_zoom)
      const cells = new Map()
      this.markers.forEach(m => {
        const w = worldPoint(m.position.lat, m.position.lng)
        const key = Math.floor((w.wx * scale) / CLUSTER_CELL_PX) + ',' + Math.floor((w.wy * scale) / CLUSTER_CELL_PX)
        let cell = cells.get(key)
        if (!cell) {
          cell = { members: [], lat: 0, lng: 0, tally: {}, sources: {} }
          cells.set(key, cell)
        }
        cell.members.push(m)
        cell.lat += m.position.lat
        cell.lng += m.position.lng
        cell.tally[m.color] = (cell.tally[m.color] || 0) + 1
        cell.sources[m.source] = (cell.sources[m.source] || 0) + 1
      })
      const out = []
      cells.forEach(cell => {
        const n = cell.members.length
        if (n < CLUSTER_MIN) {
          out.push(...cell.members)
          return
        }
        let color = null
        for (const c in cell.tally) if (!color || cell.tally[c] > cell.tally[color]) color = c
        const breakdown = Object.keys(cell.sources).sort()
          .map(s => this.esc(s) + ': ' + cell.sources[s]).join('<br/>')
        out.push({
          position: { lat: cell.lat / n, lng: cell.lng / n },
          icon: this.clusterSymbol(color, n),
          color,
          count: n,
          label: n + ' locations',
          sublabel: '',
          source: '',
          html: '<div style="font-size:14px;max-width:240px"><b>' + n + ' locations</b>'
            + '<br/><span style="color:#6e7e8f">' + breakdown + '</span>'
            + '<br/><span style="color:#6e7e8f">Click to zoom in</span></div>',
        })
      })
      return out
    },
    yardsAllOn() {
      return this.show_own && this.show_partner && this.show_public
    },
    yardsAnyOn() {
      return this.show_own || this.show_partner || this.show_public
    },
    rangeMeters() {
      return this.range_radius * 1609.34
    },
    // what the CSV button exports: My yards when that layer is the focus (vehicles off, own yards
    // on), otherwise vehicles - limited to the range circle when a range pin is set, else all
    downloadItems() {
      const yardsMode = this.show_own && !this.show_vehicles
      const src = yardsMode ? (this.own_yards || []) : (this.vehicles || [])
      const rows = src.filter(o => {
        const lat = Number(o.lat), lng = Number(o.lon)
        if (isNaN(lat) || isNaN(lng)) return false
        if (this.range_pin) return this.distanceMiles(this.range_pin, { lat, lng }) <= this.range_radius
        return true
      })
      return { yardsMode, rows }
    },
    // the range slider works in METERS so it can go as tight as 300 m; range_radius stays in miles
    // (the distance filter + circle are computed in miles), so this maps between the two
    range_radius_m: {
      get() { return Math.round(this.range_radius * 1609.34) },
      set(m) { this.range_radius = m / 1609.34 },
    },
    // show a metric label below a mile (the tight end), miles above
    rangeLabel() {
      const mi = this.range_radius
      if (mi < 1) return Math.round(mi * 1609.34) + ' m'
      return (Math.round(mi * 10) / 10) + ' mi'
    },
    rangePinIcon() {
      // distinct red dot for the range pin (data markers are colored squares)
      return {
        path: 'M 0,-7 A 7,7 0 1,1 0,7 A 7,7 0 1,1 0,-7 z',
        fillColor: '#c0392b',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.1,
      }
    },
    dedupedItems() {
      // the markers currently shown on the map (per active layers), deduped by name+position
      // so a point shown by two overlapping layers lists once
      const seen = new Set()
      const items = []
      this.markers.forEach((m, i) => {
        const key = m.label + '|' + m.position.lat + ',' + m.position.lng
        if (seen.has(key)) return
        seen.add(key)
        items.push({ ...m, i })
      })
      return items
    },
    filteredItems() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.dedupedItems
      return this.dedupedItems.filter(m => (m.label + ' ' + m.sublabel).toLowerCase().includes(q))
    },
  },
  mounted() {
    this.load_settings()
    this.load_overlay()
    this.load_vehicles()
  },
  methods: {
    place_range_pin(ev) {
      if (!ev || !ev.latLng) return
      this.range_pin = { lat: ev.latLng.lat(), lng: ev.latLng.lng() }
    },
    clear_range_pin() {
      this.range_pin = null
    },
    distanceMiles(a, b) {
      // haversine distance in miles
      const R = 3958.8
      const toRad = d => (d * Math.PI) / 180
      const dLat = toRad(b.lat - a.lat)
      const dLng = toRad(b.lng - a.lng)
      const lat1 = toRad(a.lat)
      const lat2 = toRad(b.lat)
      const h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
    },
    isolate(layerId) {
      // clicking a layer row shows only that layer (all others off); clicking a checkbox
      // just toggles that one layer (see @click.stop on the inputs)
      this.show_vehicles = layerId === 'vehicles'
      this.show_own = layerId === 'own'
      this.show_partner = layerId === 'partner'
      this.show_public = layerId === 'public'
    },
    toggle_section(section) {
      // clicking a section heading turns all of its layers on, or all off if any are on
      if (section === 'vehicles') {
        if (this.vehicles.length) this.show_vehicles = !this.show_vehicles
      } else if (section === 'yards') {
        this.set_section('yards', !this.yardsAnyOn)
      }
    },
    set_section(section, on) {
      // the All / None buttons beside a section heading
      if (section === 'yards') {
        this.show_own = on
        this.show_partner = on
        this.show_public = on
      }
    },
    pushMarker(out, y, color, title, address, phone, sub, source) {
      const lat = Number(y.lat)
      const lng = Number(y.lon)
      if (isNaN(lat) || isNaN(lng)) return
      out.push({
        position: { lat, lng },
        icon: this.pinSymbol(color),
        color,
        label: title || '',
        sublabel: address || sub || '',
        source: source || sub || '',
        html: '<div style="font-size:14px;max-width:240px">'
          + '<b>' + this.esc(title) + '</b>'
          + (source ? ' <span style="color:#6e7e8f">(' + this.esc(source) + ')</span>' : '')
          + (sub && sub !== source ? '<br/>' + this.esc(sub) : '')
          + (address ? '<br/>' + this.esc(address) : '')
          + (phone ? '<br/>' + this.esc(phone) : '')
          + '</div>',
      })
    },
    pinSymbol(color) {
      // small square markers (per Evan's demo), centered on the location
      return {
        path: 'M -3,-3 L 3,-3 L 3,3 L -3,3 Z',
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 0.6,
        scale: this.marker_size / 6, // path edge is 6 units, so rendered px = 6 × scale
      }
    },
    esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
    },
    as_of(iso) {
      // format a telematics ISO timestamp into the viewer's local time
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      return 'As of ' + d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    },
    on_zoom(zoom) {
      if (typeof zoom === 'number') this.current_zoom = zoom
    },
    zoom_into(m) {
      // a cluster bubble: step the map in on it until it breaks apart
      if (!this.$refs.overlayMap) return
      this.$refs.overlayMap.$mapPromise.then((map) => {
        map.panTo(m.position)
        map.setZoom(Math.min(map.getZoom() + 2, 15))
      })
    },
    clusterSymbol(color, n) {
      return {
        path: 'M 0,-1 A 1,1 0 1,1 0,1 A 1,1 0 1,1 0,-1 z',
        fillColor: color,
        fillOpacity: 0.88,
        strokeColor: '#ffffff',
        strokeWeight: 1.5,
        // grow the cluster bubbles proportionally with the marker-size slider (ratio 1 at default)
        scale: Math.min(9 + 3.2 * Math.log2(n), 24) * (this.marker_size / DEFAULT_MARKER_PX),
      }
    },
    openInfo(idx) {
      const m = this.displayMarkers[idx]
      if (!m) return
      this.info = { open: true, position: m.position, html: m.html }
    },
    focus_item(m) {
      // clicking a list entry zooms the map to that vehicle/yard and opens its info window
      this.info = { open: true, position: m.position, html: m.html }
      if (this.$refs.overlayMap) {
        this.$refs.overlayMap.$mapPromise.then((map) => {
          map.panTo(m.position)
          map.setZoom(14)
        })
      }
      this.drop_pin_if_yard(m)
    },
    pick_marker(idx, m) {
      this.openInfo(idx)
      this.drop_pin_if_yard(m)
    },
    drop_pin_if_yard(m) {
      // picking a YARD drops the range pin there (with the current radius) so the nearby vehicles
      // can be filtered on the map and exported; a vehicle marker just opens its info window
      if (m && m.source !== 'My vehicle' && m.position) {
        this.range_pin = { lat: m.position.lat, lng: m.position.lng }
      }
    },
    async load_overlay() {
      this.loadError = false
      try {
        const { value: { data = {} } } = await get_sos_overlay(this.user.tenant)
        this.own_yards = data.own_yards || []
        this.network_yards = data.network_yards || []
      } catch (e) {
        this.loadError = true
      }
    },
    async load_settings() {
      // only the yard-only flag matters here; the field is new, so an older API omits it
      try {
        const { value: { data = {} } } = await get_sos_settings(this.user.tenant)
        this.yard_only = data.yard_only === true
        if (this.yard_only) this.show_vehicles = false
      } catch (e) {
        this.yard_only = false
      }
    },
    async load_vehicles() {
      // whole-fleet current telematics positions (null vehicle/samsara/booking ids = full fleet)
      this.vehicles_loading = true
      try {
        const { value: { data = {} } } = await get_vehicle_map(this.user.tenant, null, null, null)
        this.vehicles = (data.results || [])
          .map(v => ({
            lat: v.gps && v.gps.latitude,
            lon: v.gps && v.gps.longitude,
            name: v.name,
            location: v.lastLocation || this.as_of(v.gps && v.gps.time),
          }))
          .filter(v => v.lat != null && v.lon != null)
      } catch (e) {
        this.vehicles = []
      } finally {
        this.vehicles_loading = false
      }
    },
    refit() {
      this.$nextTick(this.fit_to_markers)
    },
    download_csv() {
      // export the items within the range circle - My yards when that layer is the focus, else
      // vehicles (see downloadItems)
      const { yardsMode, rows } = this.downloadItems
      if (!rows.length) return
      const cell = s => {
        const t = String(s == null ? '' : s)
        return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t
      }
      const header = yardsMode
        ? ['Yard', 'Contact', 'Address', 'Phone', 'Latitude', 'Longitude']
        : ['Vehicle ID', 'Location', 'Latitude', 'Longitude']
      const rowOf = yardsMode
        ? y => [y.yard_name, y.contact_name, y.address, y.phone, y.lat, y.lon]
        : v => [v.name, v.location, v.lat, v.lon]
      const lines = [header.join(',')]
      rows.forEach(o => lines.push(rowOf(o).map(cell).join(',')))
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = yardsMode ? 'sos-yards-in-radius.csv' : 'sos-vehicles-in-radius.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    fit_to_markers() {
      // frame every marker currently shown. called after a layer / range change only: the
      // initial view is the fixed continental US (DEFAULT_CENTER / DEFAULT_ZOOM). When a range
      // pin is set, also enclose the WHOLE search circle so widening the radius zooms out to
      // keep the full circle on screen rather than letting it spill past the map edge.
      const hasPin = !!this.range_pin
      if (!this.$refs.overlayMap || (!this.markers.length && !hasPin)) return
      this.$refs.overlayMap.$mapPromise.then((map) => {
        const g = window.google && window.google.maps
        if (!g) return
        const bounds = new g.LatLngBounds()
        this.markers.forEach(m => bounds.extend(m.position))
        if (hasPin && this.rangeMeters) {
          const c = this.range_pin
          const latOff = this.rangeMeters / 111320
          const lngOff = this.rangeMeters / (111320 * Math.cos(c.lat * Math.PI / 180))
          bounds.extend(new g.LatLng(c.lat + latOff, c.lng + lngOff))
          bounds.extend(new g.LatLng(c.lat - latOff, c.lng - lngOff))
        }
        map.fitBounds(bounds, 40)
        // a single yard would otherwise land at street level
        g.event.addListenerOnce(map, 'idle', () => {
          if (map.getZoom() > 14) map.setZoom(14)
        })
      })
    },
  },
  watch: {
    // every layer / range input refits the map to what remains; data loads do not move it
    show_vehicles() { this.refit() },
    show_own() { this.refit() },
    show_partner() { this.refit() },
    show_public() { this.refit() },
    range_pin() { this.refit() },
    range_radius() { this.refit() },
  },
}
</script>

<style scoped>
.overlay-wrap {
  display: flex;
  gap: 16px;
  padding: 16px;
  align-items: flex-start;
}
.overlay-panel {
  flex: 0 0 288px;
  min-width: 0;   /* defeat the flex min-content floor so long list rows ellipsize, not widen the column */
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e6e9ec;
  border-radius: 10px;
  padding: 14px;
}
.overlay-panel-title {
  font-weight: 700;
  font-size: 16px;
  color: #1c2733;
  margin-bottom: 6px;
  text-align: left;
}
.overlay-group {
  margin-bottom: 6px;
}
.overlay-group-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8295a7;
  text-align: left;
  margin: 6px 0 2px;
}
.overlay-group-title--btn {
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 2px 0;
  font-family: inherit;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 700;
  cursor: pointer;
}
.overlay-group-title--btn:hover {
  color: #47566a;
}
.overlay-group-head {
  display: flex;
  align-items: center;
  gap: 4px;
}
.overlay-group-head .overlay-group-title--btn {
  flex: 1 1 auto;
  width: auto;
}
.overlay-mini-btn {
  flex: 0 0 auto;
  border: 1px solid #cdd6de;
  background: #fff;
  color: #47566a;
  border-radius: 4px;
  font: inherit;
  font-size: 11px;
  line-height: 1;
  padding: 3px 6px;
  cursor: pointer;
}
.overlay-mini-btn:hover:not(:disabled) {
  border-color: #8295a7;
}
.overlay-mini-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.overlay-layer {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1c2733;
  text-align: left;
  margin: 0;
  padding: 1px 0;
  cursor: pointer;
}
.overlay-layer--pending {
  color: #9aa7b4;
  cursor: default;
}
.overlay-swatch {
  width: 11px;
  height: 11px;
  border-radius: 3px;
  flex: 0 0 auto;
}
.overlay-layer-label {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.overlay-count {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  color: #6e7e8f;
  font-size: 12px;
}
/* "Shown on map" header: title left, count + download button pushed to the right */
.overlay-shown-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.overlay-shown-head .overlay-count {
  margin-left: auto;
}
.overlay-shown-dl {
  flex: 0 0 auto;
}
.overlay-hint {
  font-size: 12px;
  color: #8295a7;
  margin-top: 8px;
}
.overlay-hint--tight {
  margin-top: 2px;
  margin-left: 20px;
}
.overlay-shown {
  border-top: 1px solid #eef1f3;
  padding-top: 10px;
  margin-top: 6px;
}
.overlay-search {
  width: 100%;
  box-sizing: border-box;
  height: 32px;
  padding: 0 10px;
  margin: 6px 0 8px;
  border: 1px solid #cdd6de;
  border-radius: 6px;
  font-size: 13px;
}
.overlay-shown-list {
  max-height: 260px;
  overflow-y: auto;
}
.overlay-shown-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-bottom: 1px solid #f1f3f5;
  padding: 6px 4px;
  cursor: pointer;
  font: inherit;
}
.overlay-shown-row:hover {
  background: #f7f9fb;
}
.overlay-shown-row:last-child {
  border-bottom: none;
}
.overlay-shown-row .overlay-swatch {
  margin-top: 3px;
}
.overlay-shown-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.overlay-shown-name {
  font-size: 13px;
  color: #1c2733;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.overlay-shown-sub {
  font-size: 12px;
  color: #8295a7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.overlay-shown-src {
  flex: 0 0 auto;
  align-self: flex-start;
  margin-top: 1px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
/* range filter control */
.overlay-range-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.overlay-range-label {
  font-size: 13px;
  color: #47566a;
}
.overlay-range-clear {
  border: none;
  background: none;
  color: #c0392b;
  font-size: 15px;
  line-height: 1;
  padding: 2px 4px;
  cursor: pointer;
}
.overlay-range-clear:hover {
  color: #922b21;
}
.overlay-range-slider {
  width: 100%;
  margin-top: 2px;
}
.overlay-mapwrap {
  flex: 1 1 auto;
  min-width: 0;
}
.overlay-map {
  width: 100%;
  height: 70vh;
  min-height: 380px;
  border-radius: 10px;
  overflow: hidden;
}
.overlay-state--error {
  color: #ae3f2e;
  background: #f6e4e0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

@media (max-width: 768px) {
  .overlay-wrap {
    flex-direction: column;
  }
  .overlay-panel {
    flex: 1 1 auto;
    width: 100%;
    box-sizing: border-box;
  }
  .overlay-map {
    height: 60vh;
  }
}
</style>
